import { Button, Card, Form, Select } from "@/atoms"
import { CloseOutlined } from "@/icons"
import { useActionsWorkflow } from "@/pages/GovernancePage/applications/workflow/useWorkflow"
import cn from "classnames"
import { ActionOptionLabel } from "./ActionOptionLabel"
import { ChangeStatusAction } from "./Actions/ChangeStatusAction"
import { JiraIssueAction } from "./Actions/JiraIssueAction"
import { LabelAction } from "./Actions/LabelAction"
import { SlackMessageAction } from "./Actions/SlackMessageAction"
import { TicketIssueAction } from "./Actions/TicketIssueAction"
import { WebhookAction } from "./Actions/WebhookAction"
import styles from "./ActionsSectionForm.module.scss"
import { ActionCardProps } from "./ActionsSectionForm.types"

const ACTION_COMPONENTS = {
  label: LabelAction,
  webhook: WebhookAction,
  dismiss: ChangeStatusAction,
  reopen: ChangeStatusAction,
  jira_issue: JiraIssueAction,
  slack_message: SlackMessageAction,
  azure_boards_issue: TicketIssueAction,
  asana_task: TicketIssueAction,
  github_issue: TicketIssueAction
} as const

export const ActionCard: React.FC<ActionCardProps> = ({
  field,
  form,
  options,
  disabled,
  t,
  onRemove,
  keyPrefix,
  index,
  triggerType
}) => {
  const actionType = Form.useWatch(["actions", field.name, "type"], form)
  const ActionComponent =
    ACTION_COMPONENTS[actionType as keyof typeof ACTION_COMPONENTS]
  const params = { action_type: actionType, trigger_type: triggerType }
  const { response: actions = [], refetch } = useActionsWorkflow(params, {
    enabled: !!actionType
  })

  return (
    <Card
      key={field.key}
      className={cn("embedded-card-ks", styles.card)}
      title={
        <div className={styles.cardHeader}>
          <span>{`${t(`${keyPrefix}.fields.action.title`)} ${index + 1}`}</span>
          {onRemove && (
            <Button
              size="small"
              type="text"
              className={styles.removeButton}
              icon={<CloseOutlined />}
              onClick={onRemove}
            />
          )}
        </div>
      }
    >
      <Form.Item
        required={false}
        name={[field.name, "type"]}
        label={t(`${keyPrefix}.fields.action.title`)}
        rules={[{ required: true, message: t("error.requiredField") }]}
      >
        <Select
          placeholder={t(`${keyPrefix}.fields.action.placeholder`)}
          disabled={disabled}
          options={options.map(
            ({ key, displayName, disabled, disabledReason }) => ({
              disabled,
              label: (
                <ActionOptionLabel
                  displayName={displayName}
                  disabled={disabled}
                  disabledReason={disabledReason}
                />
              ),
              value: key
            })
          )}
          onChange={(value: string) => {
            form.setFieldValue(["actions", field.name], {
              type: value,
              endpoint: undefined
            })
          }}
        />
      </Form.Item>
      {ActionComponent && (
        <ActionComponent
          selectPlaceholder={t(
            `${keyPrefix}.fields.selectActionPlaceholder.${actionType}`
          )}
          actions={Array.isArray(actions) ? actions : []}
          t={t}
          form={form}
          refetch={refetch}
          path={field.name}
          status={actionType}
          params={params}
        />
      )}
    </Card>
  )
}
